/**
 * ScheduleDetailModal - 수업의 예약 회원 목록 + 개별 예약 취소 (폼 3)
 *
 * read(`mapScheduleDocToItem`)가 취소된 예약을 이미 필터하므로 여기 표시되는 예약은
 * 모두 활성이다. 각 행의 "예약취소"는 부모가 넘긴 `onCancelBooking(bookingId)`를 호출한다.
 * 취소 후 부모가 목록을 refetch하면 해당 예약은 사라진다.
 */

import type { StudiomateScheduleItem } from "@vlab-corporation/studiomate-api";

interface ScheduleDetailModalProps {
  isOpen: boolean;
  schedule: StudiomateScheduleItem;
  onClose: () => void;
  onCancelBooking: (bookingId: number) => void;
  isCanceling: boolean;
  errorMessage?: string | null;
}

export function ScheduleDetailModal({
  isOpen,
  schedule,
  onClose,
  onCancelBooking,
  isCanceling,
  errorMessage,
}: ScheduleDetailModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">예약 관리</h2>
        <p className="text-sm text-gray-500 mb-4">
          {schedule.date} {schedule.startTime}~{schedule.endTime} · {schedule.title} ·{" "}
          {schedule.staffName} ({schedule.currentTrainees}/{schedule.maxTrainees})
        </p>

        {errorMessage && <p className="text-sm text-red-600 mb-3">{errorMessage}</p>}

        {schedule.bookings.length === 0 ? (
          <p className="text-sm text-gray-500">예약된 회원이 없습니다.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 font-medium text-gray-600">회원</th>
                <th className="text-left py-2 font-medium text-gray-600">수강권</th>
                <th className="text-right py-2 font-medium text-gray-600">관리</th>
              </tr>
            </thead>
            <tbody>
              {schedule.bookings.map((b, idx) => (
                <tr
                  key={b.bookingId ?? `${b.memberName}-${idx}`}
                  className="border-b border-gray-100"
                >
                  <td className="py-2 text-gray-800">{b.memberName}</td>
                  <td className="py-2 text-gray-800">{b.ticketTitle ?? "-"}</td>
                  <td className="py-2 text-right">
                    {b.bookingId != null ? (
                      <button
                        aria-label={`${b.memberName} 예약취소`}
                        onClick={() => onCancelBooking(b.bookingId as number)}
                        disabled={isCanceling}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        예약취소
                      </button>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
